import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  PUBLIC_FORM_SUBMISSION_OUTCOME_KEY,
  PublicFormSubmissionOutcomeState
} from './publicFormSubmissionOutcome';

const RICH_TEXT_CLASS = '[&_p]:mb-2 [&_p:last-child]:mb-0';

const RICH_TEXT_HEADING_CLASS =
  '[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:first:mt-0 ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:first:mt-0 ' +
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 ' +
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-1';

const RICH_TEXT_LINK_CLASS =
  '[&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer hover:[&_a]:text-blue-800 [&_a]:break-all';

function decodeHtmlEntities(raw: string): string {
  if (typeof document === 'undefined') return raw;
  const el = document.createElement('div');
  el.innerHTML = raw;
  return el.innerHTML;
}

function richTextHasVisibleContent(html: unknown): boolean {
  if (html == null || typeof html !== 'string') {
    return false;
  }
  const normalized = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.length > 0;
}

function renderRichText(htmlValue: unknown, className: string) {
  const html = typeof htmlValue === 'string' ? htmlValue : '';
  if (!html.trim()) {
    return null;
  }
  return (
    <div
      className={`${className} ${RICH_TEXT_CLASS} ${RICH_TEXT_HEADING_CLASS} ${RICH_TEXT_LINK_CLASS}`}
      dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(html) }}
    />
  );
}

export default function PublicFormSubmissionDone() {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();

  const outcomeState = location.state as PublicFormSubmissionOutcomeState | null | undefined;

  const outcome = useMemo((): PublicFormSubmissionOutcomeState | null => {
    if (outcomeState && token && outcomeState.token === token) {
      return outcomeState;
    }
    if (!token || typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(PUBLIC_FORM_SUBMISSION_OUTCOME_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as PublicFormSubmissionOutcomeState & { at?: number };
      if (parsed?.token === token) {
        return parsed;
      }
    } catch {
      return null;
    }
    return null;
  }, [outcomeState, token]);

  const heading =
    typeof outcome?.formTitle === 'string' && outcome.formTitle.trim()
      ? outcome.formTitle.trim()
      : 'Submission received';

  if (!token) {
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-muted/30">
        <div className="flex-1 overflow-auto flex items-center justify-center py-10 px-4">
          <p className="text-sm text-muted-foreground">This link is not valid.</p>
        </div>
      </div>
    );
  }

  if (!outcome) {
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden bg-muted/30">
        <div className="flex-1 overflow-auto flex items-center justify-center py-10 px-4">
          <div className="panel w-full max-w-[520px] p-6 shadow-md rounded-lg border bg-background space-y-3 text-center">
            <p className="text-sm text-muted-foreground">No submission details to show.</p>
            <Link to="/" className="text-sm text-blue-600 underline">
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const showRich = richTextHasVisibleContent(outcome.successMessageHtml);
  const showDefaultNewBody = !outcome.updated && !showRich;

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-muted/30">
      <div className="flex-1 overflow-auto flex items-center justify-center py-10 px-4">
        <div className="panel w-full max-w-[520px] p-6 shadow-md rounded-lg border bg-background space-y-3 text-left">
          <h2 className="text-2xl font-semibold">{heading}</h2>
          {outcome.updated && (
            <p className="text-sm font-medium text-foreground">Submission updated</p>
          )}
          {showRich ? renderRichText(outcome.successMessageHtml, 'text-sm text-black') : null}
          {showDefaultNewBody ? (
            <p className="text-sm text-muted-foreground">Submission received.</p>
          ) : null}
          <div className="pt-2">
            <Link to="/" className="text-sm text-blue-600 hover:underline">
              Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
